import { useState } from 'react'
import {
	UnitListButton,
	MatchingUnitsPanel,
	UnitListButtonPricing,
	UnitListItem
} from '@/components/UnitListButton'

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-16'>
			<h3 className='spaces:text-14 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
				{title}
			</h3>
			{children}
		</div>
	)
}

const PRICING_MODES: UnitListButtonPricing[] = [
	'flat',
	'revenue-management',
	'hidden'
]

const MOCK_PANEL_UNITS: UnitListItem[] = [
	{
		unitNumber: '1201-A',
		floor: 12,
		availableDate: 'Sep 18',
		priceRange: '$2,150–$2,350',
		baseRent: '$2,050',
		leaseTerm: '12 Mo.',
		tags: ['Corner Unit']
	},
	{
		unitNumber: '1202-A',
		floor: 12,
		availableDate: 'Sep 18',
		priceRange: '$2,100–$2,300',
		baseRent: '$2,000',
		leaseTerm: '12 Mo.',
		hasSpecial: true,
		tags: ['Renovated', 'Top Floor']
	},
	{
		unitNumber: '0402-A',
		floor: 4,
		availableDate: 'Oct 1',
		priceRange: '$2,050–$2,200',
		baseRent: '$1,950',
		leaseTerm: '14 Mo.'
	},
	{
		unitNumber: '0803-A',
		floor: 8,
		availableDate: 'Oct 15',
		priceRange: '$2,200–$2,400',
		baseRent: '$2,100',
		leaseTerm: '12 Mo.',
		hasSpecial: true
	},
	{
		unitNumber: '0604-A',
		floor: 6,
		availableDate: 'Nov 1',
		priceRange: '$2,000–$2,150',
		baseRent: '$1,900',
		leaseTerm: '12 Mo.',
		tags: ['Pet Friendly']
	}
]

export function UnitListButtonSandbox () {
	const [pricingMode, setPricingMode] = useState<UnitListButtonPricing>('flat')
	const [hasFees, setHasFees] = useState(true)

	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Controls ── */}
			<div className='spaces:flex spaces:flex-wrap spaces:gap-16'>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<span className='spaces:text-12 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
						Pricing Mode
					</span>
					<div className='spaces:flex spaces:gap-8'>
						{PRICING_MODES.map(mode => (
							<button
								key={mode}
								onClick={() => setPricingMode(mode)}
								className={`spaces:text-12 spaces:font-default spaces:rounded-8 spaces:px-12 spaces:py-8 spaces:transition-colors spaces:border spaces:border-canvas-3 ${
									pricingMode === mode
										? 'spaces:bg-brand-subtle spaces:text-brand-on-subtle spaces:border-brand-base'
										: 'spaces:text-content-2 spaces:hover:bg-canvas-2'
								}`}
							>
								{mode}
							</button>
						))}
					</div>
				</div>
				<div className='spaces:flex spaces:flex-col spaces:gap-8'>
					<span className='spaces:text-12 spaces:font-semi spaces:text-content-3 spaces:uppercase spaces:tracking-wide'>
						Has Fees
					</span>
					<div className='spaces:flex spaces:gap-8'>
						{[true, false].map(val => (
							<button
								key={String(val)}
								onClick={() => setHasFees(val)}
								className={`spaces:text-12 spaces:font-default spaces:rounded-8 spaces:px-12 spaces:py-8 spaces:transition-colors spaces:border spaces:border-canvas-3 ${
									hasFees === val
										? 'spaces:bg-brand-subtle spaces:text-brand-on-subtle spaces:border-brand-base'
										: 'spaces:text-content-2 spaces:hover:bg-canvas-2'
								}`}
							>
								{val ? 'true' : 'false'}
							</button>
						))}
					</div>
				</div>
			</div>

			{/* ── Individual Buttons ── */}
			<Section title='UnitListButton — Individual'>
				<div className='spaces:flex spaces:flex-col spaces:gap-4 spaces:max-w-[620px] spaces:bg-canvas-4 spaces:rounded-16 spaces:p-4'>
					<UnitListButton
						unitNumber='1201-A'
						floor={12}
						availableDate='Sep 18'
						priceRange='$2,150–$2,350'
						baseRent='$2,050'
						leaseTerm='12 Mo.'
						hasSpecial
						tags={['Custom Tag', 'Custom Tag']}
						pricing={pricingMode}
						hasFees={hasFees}
						hasDisclaimerSymbol
						onClick={() => {}}
						onPricingClick={() => {}}
						onLeaseTermClick={() => {}}
					/>
					<div className='spaces:h-px spaces:bg-canvas-2 spaces:mx-4' />
					<UnitListButton
						unitNumber='0402-B'
						floor={4}
						availableDate='Oct 1'
						priceRange='$1,650–$1,800'
						baseRent='$1,550'
						leaseTerm='14 Mo.'
						tags={['Renovated']}
						pricing={pricingMode}
						hasFees={hasFees}
						hasDisclaimerSymbol={false}
						onClick={() => {}}
						onPricingClick={() => {}}
						onLeaseTermClick={() => {}}
					/>
					<div className='spaces:h-px spaces:bg-canvas-2 spaces:mx-4' />
					<UnitListButton
						unitNumber='0803-A'
						floor={8}
						priceRange='$2,200–$2,400'
						baseRent='$2,100'
						leaseTerm='12 Mo.'
						hasSpecial
						pricing={pricingMode}
						hasFees={hasFees}
						hasDisclaimerSymbol
						onClick={() => {}}
						onPricingClick={() => {}}
					/>
				</div>
			</Section>

			{/* ── MatchingUnitsPanel ── */}
			<Section title='MatchingUnitsPanel'>
				<div className='spaces:max-w-[620px]'>
					<MatchingUnitsPanel
						units={MOCK_PANEL_UNITS}
						pricing={pricingMode}
						hasFees={hasFees}
						hasDisclaimerSymbol
						onUnitClick={unit => console.log('Unit clicked:', unit)}
						onPricingClick={unit => console.log('Pricing clicked:', unit)}
						onLeaseTermClick={unit => console.log('Lease term clicked:', unit)}
					/>
				</div>
			</Section>
		</div>
	)
}
